import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import { 
  ArrowRight, 
  Play,
  Droplets,
  Wind,
  Thermometer,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Headphones,
  ExternalLink,
  Globe,
  Target,
  Leaf,
  Users,
  BookOpen,
  Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const HERO_IMAGE = `${process.env.REACT_APP_BACKEND_URL}/api/files/alma-vivu/photos/maud-classroom.png`;
const PORTRAIT_IMAGE = `${process.env.REACT_APP_BACKEND_URL}/api/files/alma-vivu/photos/maud-portrait.png`;

// Climate Exposure Game Data
const climateQuestions = [
  {
    id: 1,
    question: "Votre entreprise est-elle située dans une zone susceptible d'être touchée par des inondations ?",
    context: "Les inondations sont de plus en plus fréquentes avec le changement climatique.",
    icon: Droplets,
    options: [
      { text: "Non, nous sommes en zone sûre", points: 0, exposure: "low" },
      { text: "Peut-être, nous ne savons pas vraiment", points: 1, exposure: "medium" },
      { text: "Oui, nous avons déjà été touchés", points: 2, exposure: "high" }
    ]
  },
  {
    id: 2,
    question: "Comment gérez-vous les vagues de chaleur au sein de votre entreprise ?",
    context: "Les canicules impactent la productivité et la santé des collaborateurs.",
    icon: Thermometer,
    options: [
      { text: "Nous avons un plan d'adaptation complet", points: 0, exposure: "low" },
      { text: "Nous improvisons selon la situation", points: 1, exposure: "medium" },
      { text: "Nous n'avons pas de plan particulier", points: 2, exposure: "high" }
    ]
  },
  {
    id: 3,
    question: "Votre chaîne d'approvisionnement dépend-elle de ressources sensibles au climat ?",
    context: "Agriculture, matières premières... Beaucoup de secteurs sont vulnérables.",
    icon: Wind,
    options: [
      { text: "Non, nos fournisseurs sont diversifiés", points: 0, exposure: "low" },
      { text: "Partiellement, certains approvisionnements sont fragiles", points: 1, exposure: "medium" },
      { text: "Oui, nous dépendons fortement de ressources sensibles", points: 2, exposure: "high" }
    ]
  },
  {
    id: 4,
    question: "Avez-vous réalisé un diagnostic de vulnérabilité climatique ?",
    context: "Identifier ses risques est la première étape vers la résilience.",
    icon: AlertTriangle,
    options: [
      { text: "Oui, avec un plan d'action associé", points: 0, exposure: "low" },
      { text: "Nous y réfléchissons mais rien de concret", points: 1, exposure: "medium" },
      { text: "Non, nous n'avons jamais fait ce type d'analyse", points: 2, exposure: "high" }
    ]
  },
  {
    id: 5,
    question: "Comment anticipez-vous l'évolution réglementaire liée au climat ?",
    context: "CSRD, taxonomie... Les réglementations se renforcent rapidement.",
    icon: CheckCircle,
    options: [
      { text: "Nous sommes déjà en conformité et anticipons", points: 0, exposure: "low" },
      { text: "Nous commençons à nous y préparer", points: 1, exposure: "medium" },
      { text: "Nous découvrons ces sujets", points: 2, exposure: "high" }
    ]
  }
];

const ecosystemItems = [
  {
    title: "A Propos",
    description: "Transmettre, éclairer et donner les clés de l'action à tous les publics.",
    link: "/design-durable",
    color: "var(--terracotta)"
  },
  {
    title: "Méthodologie",
    description: "Utiliser ces clés au quotidien pour mieux s'engager.",
    link: "/innovation-responsable",
    color: "var(--maquis)"
  },
  {
    title: "Outils",
    description: "Des ateliers adaptés à chaque public pour comprendre et agir sur des sujets concrets.",
    link: "/podcast",
    color: "var(--terracotta-dark)"
  },
  {
    title: "Domaines d'intervention",
    description: "Former, transformer les métiers et engager la montée en compétences.",
    link: "/strategie-rse",
    color: "var(--maquis-dark)"
  }
];

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { getField } = usePageContent("home");
  usePageTitle("Accueil");
  const [gameStarted, setGameStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleStartGame = () => {
    setGameStarted(true);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setTotalScore(0);
  };

  const handleAnswer = (option, index) => {
    setSelectedAnswer(index);
    setIsAnimating(true);
    
    const newAnswers = {
      ...answers,
      [currentQuestion]: { ...option, questionId: climateQuestions[currentQuestion].id }
    };
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestion < climateQuestions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setIsAnimating(false);
      } else {
        const score = Object.values(newAnswers).reduce((acc, ans) => acc + ans.points, 0);
        setTotalScore(score);
        setShowResults(true);
        setIsAnimating(false);
        
        if (isAuthenticated) {
          saveQuizResult(score, newAnswers);
        }
      }
    }, 600);
  };

  const saveQuizResult = async (score, answersData) => {
    try {
      await axios.post(`${API}/quiz/result`, {
        quiz_type: "climate_exposure",
        score: climateQuestions.length * 2 - score,
        max_score: climateQuestions.length * 2,
        answers: answersData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Résultats sauvegardés !");
    } catch (error) {
      console.error("Failed to save results:", error);
    }
  };

  const getExposureLevel = (score) => {
    const maxScore = climateQuestions.length * 2;
    const percentage = (score / maxScore) * 100;
    
    if (percentage <= 33) return { level: "Faible", color: "text-[var(--maquis)]", bgColor: "bg-[var(--maquis)]/10" };
    if (percentage <= 66) return { level: "Modérée", color: "text-[var(--terracotta)]", bgColor: "bg-[var(--terracotta)]/10" };
    return { level: "Élevée", color: "text-[var(--terracotta-dark)]", bgColor: "bg-[var(--terracotta)]/20" };
  };

  const progress = ((currentQuestion + 1) / climateQuestions.length) * 100;
  const question = climateQuestions[currentQuestion];
  const QuestionIcon = question?.icon;
  const exposure = getExposureLevel(totalScore);

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen relative overflow-hidden">
        {/* Background blobs */}
        <div className="blob-bg w-[600px] h-[600px] bg-[var(--dusty-rose)] top-0 right-0"></div>
        <div className="blob-bg w-[400px] h-[400px] bg-[var(--sand)] bottom-20 left-20"></div>

        <div className="container-premium relative z-10 min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-32">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="badge-premium">
                <span className="w-2 h-2 bg-[var(--terracotta)] rounded-full"></span>
                Ajaccio, Corse
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-[var(--charcoal)] leading-[1.1] tracking-tight">
                Transmettre et mobiliser{" "}
                <span className="text-[var(--terracotta)] emphasis">aujourd'hui</span>{" "}
                pour préserver nos conditions de vie sur notre planète Terre
              </h1>

              <p className="text-lg md:text-xl text-[var(--stone)] leading-relaxed max-w-xl">
                ALMA VIVU accompagne au changement de comportement les adultes de demain et les adultes d'aujourd'hui qui souhaitent se former, monter en compétences et devenir responsables de la transformation écologique et sociale en Corse.
              </p>

              {/* Stats */}
              <div className="flex gap-12 pt-4">
                <div>
                  <div className="font-display text-4xl font-bold text-[var(--terracotta)]">7</div>
                  <div className="text-sm text-[var(--stone)] uppercase tracking-wider">Limites dépassées</div>
                </div>
                <div>
                  <div className="font-display text-4xl font-bold text-[var(--maquis)]">2030</div>
                  <div className="text-sm text-[var(--stone)] uppercase tracking-wider">Objectifs SNBC</div>
                </div>
              </div>
            </div>

            {/* Right - Photo */}
            <div className="relative hidden lg:block animate-fade-in stagger-2">
              <div className="relative">
                {/* Arch container */}
                <div className="relative w-full aspect-[3/4] rounded-t-full overflow-hidden shadow-2xl">
                  <img 
                    src={HERO_IMAGE}
                    alt="Maud - Fondatrice ALMA VIVU en formation"
                    className="w-full h-full object-cover object-center"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--maquis)]/30 to-transparent"></div>
                </div>
                
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 glass-warm rounded-2xl p-6 shadow-xl animate-float">
                  <p className="font-display text-lg font-semibold text-[var(--charcoal)]">Maud</p>
                  <p className="text-sm text-[var(--stone)]">Fondatrice ALMA VIVU</p>
                </div>

                {/* Decorative elements */}
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-[var(--terracotta)]/20 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-12 right-12 w-32 h-32 bg-[var(--maquis)]/20 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Climate Exposure Game Modal */}
      {gameStarted && !showResults && (
        <div className="fixed inset-0 bg-[var(--charcoal)]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="climate-game-modal">
          <div className="bg-[var(--bone)] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl animate-scale-in">
            {/* Header */}
            <div className="bg-[var(--maquis)] text-[var(--bone)] p-8 rounded-t-3xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold">
                  Êtes-vous exposé aux aléas climatiques ?
                </h2>
                <button 
                  onClick={() => setGameStarted(false)}
                  className="text-[var(--bone)]/80 hover:text-[var(--bone)] transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="h-2 bg-[var(--bone)]/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[var(--terracotta)] rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-[var(--bone)]/80 mt-3">
                Question {currentQuestion + 1} sur {climateQuestions.length}
              </p>
            </div>

            {/* Question */}
            <div className="p-8">
              <div className={`transition-all duration-300 ${isAnimating ? 'opacity-50 translate-x-4' : 'opacity-100'}`}>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-[var(--sand)] rounded-2xl flex items-center justify-center">
                    <QuestionIcon className="w-7 h-7 text-[var(--maquis)]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold text-[var(--charcoal)]">
                      {question.question}
                    </h3>
                    <p className="text-sm text-[var(--stone)] mt-1">{question.context}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(option, index)}
                      disabled={selectedAnswer !== null}
                      data-testid={`climate-option-${index}`}
                      className={`w-full text-left quiz-option-premium flex items-center gap-4 ${
                        selectedAnswer === index ? 'selected' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        option.exposure === 'low' ? 'bg-[var(--maquis)]/10 text-[var(--maquis)]' :
                        option.exposure === 'medium' ? 'bg-[var(--terracotta)]/10 text-[var(--terracotta)]' :
                        'bg-[var(--terracotta)]/20 text-[var(--terracotta-dark)]'
                      }`}>
                        {option.exposure === 'low' ? <CheckCircle className="w-5 h-5" /> :
                         <AlertTriangle className="w-5 h-5" />}
                      </div>
                      <span className="flex-1 font-medium text-[var(--charcoal)]">{option.text}</span>
                      <ChevronRight className="w-5 h-5 text-[var(--stone)]" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResults && (
        <div className="fixed inset-0 bg-[var(--charcoal)]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="climate-results-modal">
          <div className="bg-[var(--bone)] rounded-3xl max-w-2xl w-full shadow-2xl animate-scale-in overflow-hidden">
            <div className={`p-10 text-center ${exposure.bgColor}`}>
              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--bone)]`}>
                {exposure.level === 'Faible' ? 
                  <CheckCircle className={`w-12 h-12 ${exposure.color}`} /> :
                  <AlertTriangle className={`w-12 h-12 ${exposure.color}`} />
                }
              </div>
              <h2 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-2">
                Exposition {exposure.level}
              </h2>
              <p className="text-[var(--stone)]">
                Score de vulnérabilité : {totalScore} / {climateQuestions.length * 2}
              </p>
            </div>

            <div className="p-10">
              <p className="text-[var(--stone)] mb-8 text-center">
                {exposure.level === 'Faible' 
                  ? "Votre entreprise semble bien préparée. Continuez à anticiper !"
                  : exposure.level === 'Modérée'
                    ? "Des vulnérabilités existent. Un accompagnement pourrait vous aider."
                    : "Action recommandée pour sécuriser votre activité."
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/contact')}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  Être accompagné
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setGameStarted(false);
                    setShowResults(false);
                  }}
                  className="btn-secondary flex-1"
                >
                  Fermer
                </Button>
              </div>

              {!isAuthenticated && (
                <p className="text-center text-sm text-[var(--stone)] mt-6">
                  <Link to="/auth" className="text-[var(--terracotta)] font-medium hover:underline">
                    Créez votre espace
                  </Link> pour sauvegarder vos résultats
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manifesto Section */}
      <section className="section-premium bg-[var(--sand)]" data-testid="manifesto-section">
        <div className="container-premium">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-2">
                <div className="w-full aspect-[3/4] rounded-3xl overflow-hidden shadow-xl">
                  <img 
                    src={PORTRAIT_IMAGE}
                    alt="Maud Dupupet"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-3 space-y-6">
                <p className="font-display text-2xl md:text-3xl text-[var(--charcoal)] leading-relaxed">
                  Les jeunes d'aujourd'hui sont les adultes de demain, mais les transitions économiques et écologiques{" "}
                  <span className="text-[var(--terracotta)] emphasis">s'écrivent dès maintenant</span>.
                </p>
                <p className="text-[var(--stone)] text-lg leading-relaxed">
                  J'accompagne les enfants, les étudiants, mais aussi les adultes (enseignants, formateurs, salariés, responsables pédagogiques et citoyens) qui veulent faire bouger les choses, transmettre aux générations futures et adapter leurs compétences aux défis du XXIe siècle.
                </p>
                <p className="text-[var(--stone)] text-lg leading-relaxed">
                  À travers une pédagogie active et des méthodes d'intelligence collective, je vous donne les clés scientifiques et pratiques pour comprendre la complexité du monde et agir concrètement à votre échelle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Grid Section */}
      <section className="section-premium" data-testid="ecosystem-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <span className="badge-premium mb-6">
              L'écosystème ALMA VIVU
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)]">
              Votre parcours de <span className="emphasis">transformation</span>
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {ecosystemItems.map((item, index) => (
              <Link 
                key={index}
                to={item.link}
                className="card-premium p-8 md:p-10 group relative overflow-hidden"
                data-testid={`ecosystem-card-${index}`}
              >
                {/* Color accent */}
                <div 
                  className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ backgroundColor: item.color }}
                />
                
                <div className="relative">
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-[var(--charcoal)] mt-2 mb-4">
                    {item.title}
                  </h3>
                  <p className="text-[var(--stone)] mb-6">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium group-hover:gap-4 transition-all" style={{ color: item.color }}>
                    Découvrir <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section - Mon approche */}
      <section className="section-premium bg-[var(--cream)]" data-testid="approach-section">
        <div className="container-premium">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                Mon <span className="text-[var(--terracotta)] emphasis">approche</span>
              </h2>
            </div>

            <p className="text-lg text-[var(--stone)] leading-relaxed mb-10 text-center max-w-3xl mx-auto">
              Mon approche vise à transmettre la culture et les compétences nécessaires pour appréhender les enjeux environnementaux, sociaux et sociétaux.
            </p>

            <div className="mb-10">
              <h3 className="font-display text-xl font-semibold text-[var(--charcoal)] mb-6 text-center">
                À travers mes interventions, je vous permets de :
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { icon: Globe, text: "Comprendre la complexité des systèmes vivants (climat, biodiversité, cycles naturels, ressources)." },
                  { icon: Lightbulb, text: "Développer votre esprit critique en m'appuyant sur la rigueur scientifique pour distinguer les faits des opinions." },
                  { icon: Leaf, text: "Adopter un comportement éthique et responsable au quotidien comme dans sa vie professionnelle." },
                  { icon: Users, text: "Passer à l'action individuelle et collective pour construire un monde durable et résilient." },
                ].map((item, idx) => (
                  <div key={idx} className="card-premium p-6 flex items-start gap-4">
                    <div className="w-10 h-10 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[var(--terracotta)]" />
                    </div>
                    <p className="text-[var(--stone)] leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific Framework Section */}
      <section className="section-premium" data-testid="framework-section">
        <div className="container-premium">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                Mes boussoles <span className="text-[var(--terracotta)] emphasis">scientifiques</span> et nationales
              </h2>
              <p className="text-[var(--stone)] text-lg mt-4 max-w-3xl mx-auto">
                Pour garantir que nos actions soient efficaces et alignées avec la réalité des enjeux, mon approche pédagogique repose sur deux cadres fondamentaux :
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Limites planétaires */}
              <div className="card-premium p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[var(--maquis)]/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="w-12 h-12 bg-[var(--maquis)]/10 rounded-xl flex items-center justify-center mb-5">
                    <Globe className="w-6 h-6 text-[var(--maquis)]" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-4">
                    Les 7 limites planétaires
                  </h3>
                  <p className="text-[var(--stone)] leading-relaxed mb-6">
                    Comprendre et respecter les seuils physiques de notre planète pour préserver des conditions de vie soutenables et compatibles avec le vivant.
                  </p>
                  <a 
                    href="https://www.footprintcalculator.org/home/fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[var(--maquis)] text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-[var(--maquis-dark)] transition-colors"
                    data-testid="footprint-calculator-link"
                  >
                    De combien de planètes aurions-nous besoin ?
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* SNBC */}
              <div className="card-premium p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[var(--terracotta)]/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="w-12 h-12 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center mb-5">
                    <Target className="w-6 h-6 text-[var(--terracotta)]" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-4">
                    La Stratégie Nationale Bas Carbone (SNBC)
                  </h3>
                  <p className="text-[var(--stone)] leading-relaxed mb-6">
                    Inscrire nos choix individuels, collectifs et professionnels dans la trajectoire de décarbonation et de sobriété nécessaire pour le climat.
                  </p>
                  <a 
                    href="https://www.agenda-2030.fr/a-la-une/actualites-a-la-une/article/la-troisieme-strategie-nationale-bas-carbone-une-nouvelle-feuille-de-route-pour"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-[var(--terracotta-dark)] transition-colors"
                    data-testid="snbc-link"
                  >
                    En savoir plus
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="section-premium bg-[var(--maquis)]" data-testid="podcast-section">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-[var(--bone)]">
              <span className="badge-premium bg-[var(--bone)]/10 text-[var(--bone)] mb-6">
                <Headphones className="w-4 h-4" />
                Podcast
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
                {getField("podcast", "title", "Maquis")} <span className="text-[var(--terracotta-light)] emphasis">Vivant</span>
              </h2>
              <p className="text-[var(--bone)]/80 text-lg mb-8 leading-relaxed">
                {getField("podcast", "description", "Le podcast pour batir une entreprise durable et responsable. Rencontres, temoignages et inspirations pour transformer votre entreprise.")}
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <a 
                  href="https://podcast.ausha.co/maquis-vivant" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[var(--bone)]/10 hover:bg-[var(--bone)]/20 px-5 py-3 rounded-full transition-colors"
                >
                  <Headphones className="w-4 h-4" />
                  <span className="font-medium">Ausha</span>
                </a>
                <a 
                  href="https://open.spotify.com/show/52wol3hDjDzCs9KyYbX06j" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#1DB954]/20 hover:bg-[#1DB954]/30 px-5 py-3 rounded-full transition-colors"
                >
                  <span className="font-medium">Spotify</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a 
                  href="https://podcasts.apple.com/us/podcast/maquis-vivant-b%C3%A2tir-une-entreprise-durable-responsable/id1869635949" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[var(--bone)]/10 hover:bg-[var(--bone)]/20 px-5 py-3 rounded-full transition-colors"
                >
                  <span className="font-medium">Apple Podcasts</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <a href="https://podcast.ausha.co/maquis-vivant" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[var(--terracotta-light)] font-semibold hover:gap-3 transition-all">
                Voir tous les épisodes <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Podcast Player Card */}
            <Link to="/podcast" className="podcast-player p-8 block hover:shadow-2xl transition-shadow">
              <div className="flex items-center gap-6 mb-6">
                <div className="w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg">
                  <img 
                    src="https://image.ausha.co/LgFMa8OYmVnFcWrLy5J9bVKdN4WvkYAjwrA8wu6G_1400x1400.jpeg?t=1768570423" 
                    alt="Maquis Vivant" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[var(--stone)] mb-1">Dernier épisode</p>
                  <h3 className="font-display text-xl font-semibold text-[var(--charcoal)]">
                    {getField("podcast", "latest_episode", "Grandir par et avec les autres")}
                  </h3>
                  <p className="text-sm text-[var(--stone)]">{getField("podcast", "latest_duration", "27:36")} • {getField("podcast", "latest_date", "Fevrier 2026")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[var(--terracotta)] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 h-2 bg-[var(--sand)] rounded-full overflow-hidden">
                  <div className="h-full w-0 bg-[var(--terracotta)] rounded-full"></div>
                </div>
                <span className="text-xs text-[var(--stone)]">27:36</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-premium relative overflow-hidden" data-testid="cta-section">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
        
        <div className="container-premium relative z-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--charcoal)] mb-8">
            Prêt à transmettre<br />
            <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
          </h2>
          <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
            Faisons le point ensemble sur vos enjeux et construisons un parcours 
            adapté à votre contexte et vos attentes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <button className="btn-primary text-lg px-10 py-5">
                Prendre rendez-vous
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
